/**
 * BottomTabs — port of the bottom-content tab row from layout.py.
 * Same labels and the same active-tab orange underline as the Python.
 */
import { useState } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { NewsTab }      from '@/components/tabs/NewsTab';
import { OptionsTab }   from '@/components/tabs/OptionsTab';
import { ScreenerTab }  from '@/components/tabs/ScreenerTab';
import { HeatmapTab }   from '@/components/tabs/HeatmapTab';
import { CalendarTab }  from '@/components/tabs/CalendarTab';
import { PortfolioTab } from '@/components/tabs/PortfolioTab';
import { CorrTab }      from '@/components/tabs/CorrTab';
import { BacktestTab }  from '@/components/tabs/BacktestTab';
import { OptimizerTab } from '@/components/tabs/OptimizerTab';
import { QuantTab }     from '@/components/tabs/QuantTab';
import { DomTab }       from '@/components/tabs/DomTab';
import { GroupTab }     from '@/components/tabs/GroupTab';
import { OrdersTab }    from '@/components/tabs/OrdersTab';
import { EventsTab }    from '@/components/tabs/EventsTab';

const TABS = [
  { id: 'dom',       label: 'DOM' },
  { id: 'orders',    label: 'ORDERS' },
  { id: 'group',     label: 'GROUP' },
  { id: 'news',      label: 'NEWS' },
  { id: 'options',   label: 'OPTIONS' },
  { id: 'screener',  label: 'SCREENER' },
  { id: 'heatmap',   label: 'HEATMAP' },
  { id: 'calendar',  label: 'CALENDAR' },
  { id: 'portfolio', label: 'PORTFOLIO' },
  { id: 'corr',      label: 'CORR' },
  { id: 'backtest',  label: 'BACKTEST' },
  { id: 'optimizer', label: 'OPTIM' },
  { id: 'quant',     label: 'QUANT' },
  { id: 'events',    label: 'EVENTS' },
] as const;

type TabId = typeof TABS[number]['id'];

export function BottomTabs() {
  const [active, setActive] = useState<TabId>('news');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="tab-row">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`tab-btn${active === t.id ? ' active' : ''}`}
            onClick={() => setActive(t.id)}
          >
            {t.label}
          </button>
        ))}
        {/* Product identity lives here in the status bar — visible, on-brand,
            and out of the crowded top ticker/nav strip. Mirrors the marketing
            crumbs: Terminal is the Proof-of-Execution surface. */}
        <span
          title="Immutable audit of what was done — Proof of Execution"
          style={{
            marginLeft: 'auto', alignSelf: 'center', paddingRight: 12,
            fontFamily: "Consolas,'Courier New',monospace", fontSize: 9,
            letterSpacing: 1.5, color: '#6bb6ab', whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          PROOF OF EXECUTION
        </span>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '6px 10px' }}>
        {/* Per-tab boundary. key={active} resets it on tab switch, so ONE tab's
            crash is contained to that panel -- the chart, order entry and kill
            switch stay live -- instead of taking down the whole terminal. */}
        <ErrorBoundary key={active} label={active.toUpperCase()}>
          {active === 'dom'       && <DomTab />}
          {active === 'orders'    && <OrdersTab />}
          {active === 'group'     && <GroupTab />}
          {active === 'news'      && <NewsTab />}
          {active === 'options'   && <OptionsTab />}
          {active === 'screener'  && <ScreenerTab />}
          {active === 'heatmap'   && <HeatmapTab />}
          {active === 'calendar'  && <CalendarTab />}
          {active === 'portfolio' && <PortfolioTab />}
          {active === 'corr'      && <CorrTab />}
          {active === 'backtest'  && <BacktestTab />}
          {active === 'optimizer' && <OptimizerTab />}
          {active === 'quant'     && <QuantTab />}
          {active === 'events'    && <EventsTab />}
        </ErrorBoundary>
      </div>
    </div>
  );
}
